import { PermissionEnum, User } from '@sr-sdks/api-sdk-axios';
import { userService } from '../reducers/permissions/userSlice';
import { workspaceService } from '../reducers/workspaces/workspaceSlice';
import { useTypedSelector } from '../stateStore';
import { AuthHelper } from '../utils/auth/authHelper';

/**
 * Check if a user has specific permissions within a workspace.
 *
 * @param requiredPermissions - The permission keys to check.
 * @param user - If not provided, the current user will be used.
 * @param workspaceId - If not provided, the current workspace will be used.
 * @returns `boolean` - `true` only if all permissions are present.
 *
 * @example
 * // Check if user has 'view-teammates' permission
 * const canViewTeammates = useCheckUserPermission(user, 'view-teammates', workspaceId);
 */
export const useCheckUserPermissions = (
  requiredPermissions: PermissionEnum[],
  user?: User,
  workspaceId?: string,
): boolean => {
  const currentUser = useTypedSelector(userService.selectors.userMe);
  const currentWorkspace = useTypedSelector(
    workspaceService.selectors.workspace,
  );

  // No permissions to check.
  if (requiredPermissions.length === 0) return false;

  // If not provided, use the current user.
  if (!user) user = currentUser;
  if (!user) return false;

  // If not provided, use the current workspace.
  if (!workspaceId) workspaceId = currentWorkspace?._id;
  if (!workspaceId) return false;

  return AuthHelper.forUser(user).currentUserHasPermissionOnWorkspace(
    workspaceId,
    ...requiredPermissions,
  );
};
